import React from 'react';
import moment from 'moment';

// 3rd party
import Loader from "calcite-react/Loader";
import Panel from "calcite-react/Panel";
import Popover from "calcite-react/Popover";
import XIcon from "calcite-ui-icons-react/XIcon";
import CalendarIcon from "calcite-ui-icons-react/CalendarIcon";

// aiim.base
import ItemReference from '../../../../../aiim/base/ItemReference';

// aiim.datasets
import FieldNames from '../../../../../aiim/datasets/FieldNames';

// aiim.util
import { findFieldName, getAttributeValue } from '../../../../../aiim/util/aiimUtil';
import { escSqlQuote } from '../../../../../aiim/util/selectionUtil';

// components.main.Actions.More.BookWorkspace.WorkspaceReservation
import { formatDate, makeReservationICS } from './WorkspaceReservation/workspaceReservationUtil';

// components.main.Events
import { getZulu } from '../../../Events/dateUtil';

// components.util
import * as component from '../../../../util/component';
import * as aiimUtil from "../../../../../aiim/util/aiimUtil";

// context
import Context from '../../../../../context/Context';
import Topic from '../../../../../context/Topic';
import SignInIcon from "calcite-ui-icons-react/SignInIcon";
import SignOutIcon from "calcite-ui-icons-react/SignOutIcon";
import Button from "calcite-react/Button";

import { ModalController }  from "../../../../../../src/common/components/Modal/index";
import { cancelBooking, updateCheckInOut } from "./WorkspaceReservation/OfficeHotelingInterface";
import { getBookingSystem } from "./WorkspaceReservation/workspaceReservationUtil";

// util
import CalendarSelector from '../../../../../util/CalendarSelector';
import { stringFormatter } from '../../../../../util/formatUtil';
import { makeEventInfo } from '../../../../../util/calendarUtil';
import Icons from '../../../../util/Icons';
import { isNetworkError } from '../../../../../util/networkUtil';

const CSS = {
  bookingCardList: "i-booking-card-list",
  bookingCard: "i-booking-card",
  locationDescription: "i-booking-location-description",
  button: "i-book-button",
  buttonCheckIn: "i-book-card-button-checkIn",
  buttonCheckOut: "i-book-card-button-checkOut",
  actionGroup: "i-action-group",
  actionSubGroup: "i-action-subgroup",
  addToCalendar: "i-booking-add-calendar",
  cancel: "i-booking-cancel",
  descriptionContent: "i-booking-description",
};


export default class MyBookings extends React.Component {

  listReminders = [];
  APPROVED = 1;
  CHECKED_IN = 4;

  constructor(props) {
    super(props);

    this.state = component.newState({
      bookings: [],
      isWorking: true
    });
  }

  componentDidMount() {
    this._getUserReservations();
    component.own(this, [
      Topic.subscribe(Topic.RenderBookingList, info => {
        this._getUserReservations(info);
      })
    ])
  }

  _getUserReservations = async () => {
    const reservations = Context.getInstance().aiim.datasets.reservations;
    if (!reservations) {
      this.setState({
        isWorking: false
      });
      return;
    }

    const username = Context.getInstance().user.getUsername();
    const { reservedForUsernameField, endTimeField } = reservations;

    if (!username || !reservedForUsernameField) {
      this.setState({
        isWorking: false
      });
      return;
    }

    try {
      const now = getZulu(new Date());
      const whereClauses = [
        `${reservedForUsernameField} = '${escSqlQuote(username)}'`,
        `${endTimeField} > TIMESTAMP '${escSqlQuote(now)}'`
      ];
      const myReservations = await reservations.query(whereClauses.join(" AND "));
      const bookings = (myReservations && myReservations.features) || [];
      const hotelBookings = await this.filterHotelReservations(bookings);

      this.setState({
        bookings: hotelBookings,
        isWorking: false
      });
    } catch(e) {
      console.error("Unable to query for user reservations", e);
      if (e && isNetworkError(e.message)) {
        const i18n = Context.getInstance().i18n;
        Topic.publishNetworkError(i18n.meetingRooms.issues.m7);
      } else {
        Topic.publishErrorAccessingData();
      }
      this.setState({
        bookings: [],
        isWorking: false
      });
    }
  }

  filterHotelReservations = async (bookings) => {
    if (!bookings || !bookings.length) {
      return [];
    }

    const unitsDataset = Context.getInstance().aiim.datasets.units;
    const unitFields = unitsDataset.layer2D && unitsDataset.layer2D.fields;
    const hotelBookings = [];

    // Gather the unit IDs from the reservations to make a query
    const unitIds = bookings
      .map((booking) => {
        const bookingAttrs = booking.attributes;
        const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
        return `'${escSqlQuote(unitId)}'`;
      });
    // To make sure we don't add duplicate ids to the query
    // Ex: UNIT_ID IN ('1', '1', '2', '2') -> UNIT_ID IN ('1', '2')
    const uniqueIds = [...new Set(unitIds)];

    const assignmentTypeField = findFieldName(unitFields, FieldNames.UNITS_SPACE_ASSIGNMENT_TYPE);
    const whereClauses = [
      `${unitsDataset.uidField} IN (${uniqueIds.join(", ")})`,
      `${assignmentTypeField} = 'hotel'`
    ];
    const where = `${whereClauses.join(" AND ")}`;

    try {
      const results = await unitsDataset.query(where);
      const units = (results && results.features) || [];

      // Filter units by those that are hotels
      const hotelingUnitIds = units.map((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID));

      // Find the bookings that have hoteling unit IDs
      bookings.forEach((booking) => {
        const bookingAttrs = booking.attributes;
        const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
        if (hotelingUnitIds.includes(unitId)) {
          const unit = units.find((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID) === unitId);
          hotelBookings.push({ booking, unit });
        }
      });
    } catch(e) {
      console.error(e);
    }

    return hotelBookings;
  }

  sortBookings = (bookings) => {
    return bookings.sort((resA, resB) => {
      const attrsA = resA.booking.attributes;
      const attrsB = resB.booking.attributes;

      const startTimeA = getAttributeValue(attrsA, FieldNames.START_TIME);
      const startTimeB = getAttributeValue(attrsB, FieldNames.START_TIME);

      if (startTimeA > startTimeB) {
        return 1;
      } else if (startTimeA < startTimeB) {
        return -1
      }
      return 0;
    });
  }

  filterCurrentBookings = (bookings) => {
    return bookings.filter((reservation) => {
      const attrs = reservation.booking.attributes;
      const endTime = getAttributeValue(attrs, FieldNames.END_TIME);

      const now = moment();
      const mEndTime = moment(endTime);

      return mEndTime > now;
    });
  }

  getRemindersList=(sortedBookings)=>{
    if(!sortedBookings) return;
    const currentTime = new Date().getTime();
    this.listReminders = [];
    for(let i=0;i<sortedBookings.length; i++) {
      const attribute = sortedBookings[i].booking.attributes;
      const startTime = getAttributeValue(attribute, FieldNames.START_TIME);
      const endTime = getAttributeValue(attribute, FieldNames.END_TIME);
      const state = getAttributeValue(attribute, FieldNames.STATE);
      const startTimeM = moment(startTime);
      const currentTimeM = moment(currentTime);
      const elapsed = startTimeM.diff(currentTimeM, 'seconds');
      if((state === this.APPROVED) && (((0 <= elapsed) && (elapsed <= 900)) ||
          ((startTime <= currentTime) && ( currentTime <= endTime)))){
        this.listReminders.push(attribute);
      }
    }
    if(this.listReminders && this.listReminders.length > 0) this.showPopup();
  }

  makeBookingList = (bookings) => {
    const filteredBookings = this.filterCurrentBookings(bookings);
    const sortedBookings = this.sortBookings(filteredBookings);
    this.getRemindersList(sortedBookings);
    let myBookingsList = [];
    for (let i=0;i<sortedBookings.length;i++) {
      const data = sortedBookings[i];
      const reservation = data.booking;
      const state = aiimUtil.getAttributeValue(reservation.attributes, FieldNames.STATE);
      let showButton;
      if(state === this.CHECKED_IN) showButton = "checkOut";
      else if(state === this.APPROVED) showButton = "checkIn";
      if(state === this.APPROVED || state === this.CHECKED_IN) {
        myBookingsList.push(
          <MyBookingsCard 
            showButton={showButton} 
            key={i} 
            reservation={reservation}
            data={data} 
          />
        );
      }
    }

    return myBookingsList;
  }

  showPopup=()=>{
    const i18n = Context.getInstance().i18n;
    const remindersList = this.listReminders;
    let reminderUnitName = [];
    this.listReminders.forEach((reminder)=> {
      reminderUnitName.push(getAttributeValue(reminder, FieldNames.UNIT_NAME));
    })
    reminderUnitName = reminderUnitName.join(', ');

    let message = i18n.more.bookWorkspace.checkInOut.reminder.newMessage;
    message = message.replace("{units}", reminderUnitName);

    let options = {
      title: i18n.more.bookWorkspace.checkInOut.reminder.caption,
      okLabel: i18n.more.bookWorkspace.checkInOut.reminder.close,
      message: message,
      showOkCancel: true,
      hideCancel : true,
      closeOnOK: true
    };
    if (remindersList && remindersList.length === 1) {
      const reservations = Context.instance.aiim.datasets.reservations;
      const bookingSystem = getBookingSystem();
      const attributes = this.listReminders[0];
      attributes[reservations.checkInTimeField] = new Date().getTime();
      let objectIdField;
      if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
        objectIdField = reservations.layer2D.objectIdField
      }
      const objectID = getAttributeValue(attributes, objectIdField);

      const unitName = attributes[reservations.unitNameField];
      // @todo HARD-CODED STRING
      const template = "Your booking for {unit} is ready for check in. Check in when you arrive at the location.";
      const message = stringFormatter(template, { unit: unitName });

      options.message = message;
      options["okLabel"] = i18n.more.bookWorkspace.checkIn;
      options["hideCancel"] = false;
      // @todo HARD-CODED STRING
      options["cancelLabel"] = "Not Now";

      ModalController.confirm(options)
      .then(result => {
        if(result && result.ok) {
          updateCheckInOut(bookingSystem, attributes, "checkIn")
          .then((res) => {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] &&
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                Topic.publish(Topic.ShowToast,{
                message: i18n.more.bookWorkspace.checkInOut.checkIn.toastMessage
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
            })
          .catch(error => {
            console.error(error);
            Topic.publishErrorUpdatingData(error)
          })
        }
      })
      .catch(error => {
        console.error(error);
        Topic.publishErrorUpdatingData(error)
      })
    } else ModalController.confirm(options);
  }

  renderBookingCards() {
    const i18n = Context.getInstance().i18n;
    const { bookings } = this.state;
    const bookingList = this.makeBookingList(bookings);

    return bookingList && bookingList.length > 0 ? (
      <ul className={CSS.bookingCardList}>
        {bookingList}
      </ul>
    ) : (
      <div style={{ padding: '0.7rem 1rem' }}>{i18n.more.bookWorkspace.noBookings}</div>
    );
  }

  renderWorking() {
    return (<Loader sizeRatio={1} style={{ marginTop: '3rem' }} />);
  }

  render() {
    const { isWorking } = this.state;
    return isWorking ? this.renderWorking() : this.renderBookingCards();
  }
}

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

class MyBookingsCard extends React.Component {
  _componentId;
  _itemRef;

  APPROVED = 1;
  CHECKED_IN = 4;

  constructor(props) {
    super(props);
    this.state = component.newState({
      showButton: this.props.showButton,
      hideCheckIn: false,
      popoverOpen: false
    });
    this._componentId = component.nextId("booking-");
    this._itemRef = React.createRef();
  }

  componentDidMount() {
    this._startupApp();
    component.own(this, [
      Topic.subscribe(Topic.InfoPanelClosed, params => {
        if (this._componentId === params.componentId) {
          this._focus();
        }
      })
    ]);
  }

  componentWillUnmount(){
    component.componentWillUnmount(this);
  }

  _startupApp = () => {
    const { data } = this.props;
    const reservation = data.booking;
    const attributes = reservation.attributes;
    const state = aiimUtil.getAttributeValue(attributes, FieldNames.STATE);
    this.disableCheckIn();

    if(state === this.CHECKED_IN) {
      this.setState({
        showButton : "checkOut"
      })
    }else if(state === this.APPROVED) {
      this.setState({
        showButton : "checkIn"
      })
    }
  }

  _focus() {
    if (this._itemRef && this._itemRef.current && this._itemRef.current.focus) {
      this._itemRef.current.focus()
    }
  }

  _bookingCardClicked = () => {
    const { data } = this.props;
    const unit = data.unit;
    const source = Context.instance.aiim.datasets.units.getSource();

    const item = new ItemReference();
    item.fromSearchResult(source.key, { feature: unit, name: source.getTitle(unit) });
    const feature = item.getFeature();

    Topic.publish(Topic.RefreshDetails, { feature: feature });
    Topic.publish(Topic.ShowSearchResult, {
      sourceKey: source.key,
      searchResult: item.searchResult,
      zoom: true,
      highlight: true,
      trackRecent: true,
      componentId: this._componentId
    });
  }

  showServerErrorPopup = (type) => {
    const { data } = this.props;
    const reservation = data.booking;
    const attributes = reservation.attributes;
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);
    const reservations = Context.instance.aiim.datasets.reservations;
    const bookingSystem = getBookingSystem();
    const i18n = Context.getInstance().i18n;
    let message;

    if(type === "checkIn") {
      message = i18n.more.bookWorkspace.checkInOut.checkIn.serverErrorMessage;
    } else if (type === "checkOut") {
      message = i18n.more.bookWorkspace.checkInOut.checkOut.serverErrorMessage;
    } else if (type === "cancel") {
      message = i18n.more.bookWorkspace.checkInOut.cancelBooking.serverErrorMessage;
    }
    message = message.replace("{unit}", unitName);

    const options = {
      title: i18n.more.bookWorkspace.checkInOut.error,
      message: message,
      okLabel: i18n.more.bookWorkspace.checkInOut.tryAgain,
      showOkCancel: true,
      hideCancel : false,
      closeOnOK: true,
      cancelLabel: i18n.more.bookWorkspace.checkInOut.close
    };

    ModalController.confirm(options)
    .then(result => {
      if(result.ok) {
        let objectIdField;
        if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
          objectIdField = reservations.layer2D.objectIdField;
        }
        const objectID = getAttributeValue(attributes, objectIdField);
        if(type === "cancel") {
          cancelBooking(bookingSystem, attributes)
          .then((res) => {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] && 
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                  Topic.publish(Topic.RenderBookingList, {});
                  Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
                }
            })
          .catch(error => {
            if(error && isNetworkError(error.message)) {
              this.showServerErrorPopup(type);
            } else {
              Topic.publishErrorUpdatingData();
            }
            console.error(error);
          })
        } else {
          if(type === "checkOut") attributes[reservations.checkOutTimeField] = new Date().getTime();
          if(type === "checkIn") attributes[reservations.checkInTimeField] = new Date().getTime();

          updateCheckInOut(bookingSystem, attributes, type)
          .then((res)=> {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] && 
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                let msg = i18n.more.bookWorkspace.checkInOut.checkOut.toastMessage;
                if(type === "checkIn") msg = i18n.more.bookWorkspace.checkInOut.checkIn.toastMessage;
                Topic.publish(Topic.ShowToast,{
                  message: msg
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
          })
          .catch(error => {
            if(error && isNetworkError(error.message)) {
              this.showServerErrorPopup(type);
            } else {
              Topic.publishErrorUpdatingData();
            }
            console.error(error);
          })
        }
      }
    })
    .catch(error => {
      console.error(error);
      Topic.publishErrorUpdatingData()
    })
  }

  handleToggle = () => {
    const { data } = this.props;
    const reservation = data.booking;
    const attributes = reservation.attributes;
    const i18n = Context.getInstance().i18n;
    const reservations = Context.instance.aiim.datasets.reservations;
    let objectIdField;
    if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
      objectIdField = reservations.layer2D.objectIdField
    }

    const bookingSystem = getBookingSystem();
    if(this.props && this.props.showButton === "checkOut") {
      const options = {
        title: i18n.more.bookWorkspace.checkInOut.checkOut.caption,
        message: i18n.more.bookWorkspace.checkInOut.checkOut.message,
        okLabel: i18n.more.bookWorkspace.checkInOut.checkOut.yes,
        cancelLabel: i18n.more.bookWorkspace.checkInOut.checkOut.no,
        closeOnOK: true,
        closeOnCancel: true
      };
      ModalController.confirm(options)
      .then(result => {
        if(result.ok) {
          attributes[reservations.checkOutTimeField] = new Date().getTime();
          const objectID = getAttributeValue(attributes,objectIdField);

          updateCheckInOut(bookingSystem, attributes, "checkOut")
          .then((res)=> {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] &&
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                Topic.publish(Topic.ShowToast,{
                  message: i18n.more.bookWorkspace.checkInOut.checkOut.toastMessage
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
          })
          .catch(error => {
            if(error && isNetworkError(error.message)) {
              this.showServerErrorPopup("checkOut");
            } else {
              Topic.publishErrorUpdatingData(error);
            }
            console.error(error);
          })
        }
      })
      .catch(error => {
        console.error(error);
        Topic.publishErrorUpdatingData(error)
      })
    }else if( this.props && this.props.showButton === "checkIn") {

      const unitName = attributes && aiimUtil.getAttributeValue(attributes, FieldNames.UNIT_NAME);
      const message = i18n.more.bookWorkspace.checkInOut.checkIn.newMessage.replace("{unit}", unitName);

      const options = {
        title: i18n.more.bookWorkspace.checkInOut.checkIn.caption,
        message: message,
        okLabel: i18n.more.bookWorkspace.checkInOut.checkIn.yes,
        cancelLabel: i18n.more.bookWorkspace.checkInOut.checkIn.no,
        closeOnOK: true,
        closeOnCancel: true
      };
      ModalController.confirm(options)
      .then(result => {
        if(result.ok) {
          attributes[reservations.checkInTimeField] = new Date().getTime();
          const objectID = getAttributeValue(attributes,objectIdField);

          updateCheckInOut(bookingSystem, attributes, "checkIn")
          .then((res)=> {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] &&
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                Topic.publish(Topic.ShowToast,{
                  message: i18n.more.bookWorkspace.checkInOut.checkIn.toastMessage
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
          })
          .catch(error => {
            if(error && isNetworkError(error.message)) {
              this.showServerErrorPopup("checkIn");
            } else {
              Topic.publishErrorUpdatingData(error);
            }
            console.error(error);
          })
        }
      })
      .catch(error => {
        console.error(error);
        Topic.publishErrorUpdatingData(error)
      })
    }
  }

  disableCheckIn = () => {
    const { data } = this.props;
    const reservation = data.booking;
    const attributes = reservation.attributes;
    const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
    const checkInTime = getAttributeValue(attributes, FieldNames.CHECK_IN_TIME);
    const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
    const state = getAttributeValue(attributes, FieldNames.STATE);
    const currentTime = new Date().getTime();
    const startTimeM = moment(startTime);
    const currentTimeM = moment(currentTime);

    const elapsed = startTimeM.diff(currentTimeM, 'seconds');

    if(!checkInTime) {
      if((state === this.APPROVED) &&
          (((0 <= elapsed) && (elapsed <= 900)) || ((startTime <= currentTime) && ( currentTime <= endTime)))){
        this.setState({
          hideCheckIn : false
        })
      } else {
        this.setState({
          hideCheckIn : true
        })
      }
    }else {
      this.setState({
        hideCheckIn: false
      })
    }
  }

  _cancelClicked = () => {
    const { data } = this.props;
    const reservation = data.booking;
    const bookingSystem = getBookingSystem();
    const attributes = reservation.attributes;
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);
    const i18n = Context.getInstance().i18n;
    const reservations = Context.getInstance().aiim.datasets.reservations;
    let objectIdField;
    if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
      objectIdField = reservations.layer2D.objectIdField
    }
    const objectID = getAttributeValue(attributes,objectIdField);

    const options = {
      title: i18n.more.bookWorkspace.checkInOut.cancelBooking.modalTitle,
      message: i18n.more.bookWorkspace.checkInOut.cancelBooking.message,
      okLabel: i18n.more.bookWorkspace.checkInOut.cancelBooking.yes,
      cancelLabel: i18n.more.bookWorkspace.checkInOut.cancelBooking.no,
      showOKCancel: true,
      closeOnOK: true,
      closeOnCancel: true
    };
    ModalController.confirm(options)
    .then(result => {
      if(result && result.ok) {
        cancelBooking(bookingSystem, attributes)
        .then((res) => {
          if(res && res.updateFeatureResults && res.updateFeatureResults[0] &&
            res.updateFeatureResults[0].error){
              Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
            } else {
                Topic.publish(Topic.ShowToast,{
                  message: i18n.more.bookWorkspace.checkInOut.cancelBooking.toastMessage
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
          })
        .catch(error => {
          if(error && error.message === "Failed to fetch") {
            this.showServerErrorPopup("cancel");
          } else {
            Topic.publishErrorUpdatingData(error);
          }
          console.error(error);
          if (error && isNetworkError(error.message)) {
            const i18n = Context.getInstance().i18n;
            const template = i18n.meetingRooms.issues.m9;
            const message = stringFormatter(template, { unit: unitName });
            Topic.publishNetworkError(message)
          } else {
            Topic.publishErrorUpdatingData();
          }
        })
      }
    })
    .catch(error => {
      console.error(error);
      Topic.publishErrorUpdatingData(error)
    })
  }

  _onKeyDown = (event) => {
    const ENTER = 13;
    const SPACE = 32;

    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      event.preventDefault();
      this._bookingCardClicked();
    }
  }

  _togglePopover = () => {
    this.setState({ popoverOpen: !this.state.popoverOpen });
  }

  render() {
    const i18n = Context.getInstance().i18n;
    const { data, showButton } = this.props;
    const reservation = data.booking;
    const unit = data.unit;
    const { popoverOpen } = this.state;
    const reservations = Context.getInstance().aiim.datasets.reservations;
    const levels = Context.getInstance().aiim.datasets.levels;
    const attributes = reservation.attributes;
    const floorField = reservations.levelIdField;

    // Field values to display
    const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
    const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);

    // Level/Facility info
    const levelId = getAttributeValue(attributes, floorField);
    const levelData = levels.getLevelData(levelId);
    const levelName = levelData.levelName;
    const facilityName = levelData.facilityName;
    const template = i18n.more.bookWorkspace.levelDescription;
    const description = stringFormatter(template, {
      level: levelName,
      facility: facilityName
    });

    // Format a date string from the start and end times
    const mStartTime = moment(startTime);
    const mEndTime = moment(endTime);
    const isAllDay = getAttributeValue(attributes, FieldNames.ALL_DAY);
    const formattedDate = formatDate(mStartTime, mEndTime, isAllDay);

    // Check state to see if cancel button is needed
    const state = getAttributeValue(attributes, FieldNames.STATE);

    let label, icon, cls;
    if (showButton && showButton === "checkIn") {
      label = i18n.more.bookWorkspace.checkInOut.checkIn.caption;
      icon = (<SignInIcon size={24}/>);
      cls = CSS.buttonCheckIn;
    } else {
      label = i18n.more.bookWorkspace.checkInOut.checkOut.caption;
      icon = (<SignOutIcon size={24}/>);
      cls = CSS.buttonCheckOut;
    }

    const addToCalendarButton = (
      <button
        className={CSS.addToCalendar}
        data-id={"calendar"}
        onClick={this._togglePopover}
      >
        {Icons.calendarPlus()}
        {i18n.general.add}
      </button>
    );

    const cancelButton = (
      <button
        className={CSS.cancel}
        data-id={"cancel"}
        onClick={this._cancelClicked}
      >
        <XIcon />
        {i18n.general.cancel}
      </button>
    );

    const reservationsDataset = Context.getInstance().aiim.datasets.reservations;
    const unitsDataset = Context.getInstance().aiim.datasets.units;
    const calendarParams = makeEventInfo(reservationsDataset, reservation, unitsDataset, unit);

    return (
      <li className={CSS.bookingCard}>
        <div className="i-book-card-container">
          <div
            id={this._componentId}
            className={CSS.descriptionContent}
            ref={this._itemRef}
            role="button"
            tabIndex="0"
            onClick={this._bookingCardClicked}
            onKeyDown={this._onKeyDown}
          >
            <h3>{formattedDate}</h3>
            <h4>{unitName}</h4>
            <h4 className={CSS.locationDescription}>{description}</h4>
          </div>
          <div className={CSS.actionGroup}>
            {!this.state.hideCheckIn ? (
              <Button
                className={cls}
                icon={icon}
                iconPosition="before"
                onClick={this.handleToggle}>
                {label}
              </Button>
            ): (
              <div style={{ width: 'calc(100%/3)' }}></div>
            )}
            {/* <div className={CSS.actionSubGroup}> */}
              <CalendarSelector
                onRequestClose={this._togglePopover}
                open={popoverOpen}
                targetEl={addToCalendarButton}
                params={calendarParams}
              />
              {state === this.CHECKED_IN ? null : cancelButton}
            {/* </div> */}
          </div>
        </div>
      </li>
    );
  }
}
